import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewApi, errorMessage } from '../api';
import { Eyebrow, StatusPill, formatDate } from '../ui';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'changes_requested', label: 'Changes requested' },
    { value: 'approved', label: 'Approved' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'not_started', label: 'Not started' }
];

const selectClass = 'rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20';

export default function ChallengeReviews() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [challengeId, setChallengeId] = useState('');
    const [status, setStatus] = useState('all');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [nudging, setNudging] = useState(null);

    useEffect(() => {
        const id = setTimeout(() => setQuery(search), 250);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        let cancelled = false;
        reviewApi.queue({ challenge_id: challengeId || undefined, status, search: query || undefined })
            .then((d) => !cancelled && setData(d))
            .catch((err) => !cancelled && setError(errorMessage(err, 'Could not load the review queue')));
        return () => { cancelled = true; };
    }, [challengeId, status, query]);

    const kpis = useMemo(() => {
        const c = (data && data.counts) || {};
        return [
            { label: 'Awaiting review', value: c.submitted ?? '—', status: 'submitted' },
            { label: 'Changes requested', value: c.changes_requested ?? '—', status: 'changes_requested' },
            { label: 'Approved', value: c.approved ?? '—', status: 'approved' },
            { label: 'Not started', value: c.not_started ?? '—', status: 'not_started' }
        ];
    }, [data]);

    const nudge = async (row) => {
        setNudging(`${row.challenge_id}:${row.intern_user_id}`);
        try {
            await reviewApi.nudge({ challenge_id: row.challenge_id, intern_user_id: row.intern_user_id });
            toast.success(`Reminder sent to ${row.student_name}`);
        } catch (err) {
            toast.error(errorMessage(err, 'Could not send the reminder'));
        } finally {
            setNudging(null);
        }
    };

    return (
        <div className="mx-auto w-full max-w-6xl space-y-6 pb-12">
            <header>
                <Eyebrow>Practical challenges</Eyebrow>
                <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">Reviews queue</h1>
                <p className="mt-1 text-sm text-ink-600">Open a submission, leave comments, then approve or return it for changes.</p>
            </header>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {kpis.map((k) => (
                    <button
                        key={k.label} type="button" onClick={() => setStatus(status === k.status ? 'all' : k.status)}
                        className={`rounded-2xl border bg-white px-5 py-4 text-left transition-colors ${status === k.status ? 'border-brand-600 ring-1 ring-brand-600/40' : 'border-border hover:border-ink-300'}`}
                    >
                        <p className="text-xs text-ink-500">{k.label}</p>
                        <p className="mt-1 text-2xl font-bold text-ink-950">{k.value}</p>
                    </button>
                ))}
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <label className="flex items-center gap-2 text-sm font-medium text-ink-800">
                    Challenge
                    <select className={selectClass} value={challengeId} onChange={(e) => setChallengeId(e.target.value)}>
                        <option value="">All challenges</option>
                        {(data ? data.challenges : []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-ink-800">
                    Status
                    <select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </label>
                <input
                    type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student…"
                    aria-label="Search student" className={`${selectClass} min-w-0 flex-1`}
                />
            </div>

            {error && <p className="text-sm text-red-700">{error}</p>}

            <div className="overflow-hidden rounded-2xl border border-border bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-sm">
                        <thead className="border-b border-border text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                            <tr>
                                <th className="px-5 py-3">Student</th>
                                <th className="px-5 py-3">Challenge</th>
                                <th className="px-5 py-3">Submitted</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Score</th>
                                <th className="px-5 py-3"><span className="sr-only">Action</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {!data && !error && [1, 2, 3].map((i) => (
                                <tr key={i}><td colSpan={6} className="px-5 py-4"><div className="h-5 animate-pulse rounded bg-ink-100" /></td></tr>
                            ))}
                            {data && data.rows.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-10 text-center text-ink-500">No students match these filters.</td></tr>
                            )}
                            {data && data.rows.map((row) => (
                                <tr key={`${row.challenge_id}:${row.intern_user_id}`} className="hover:bg-ink-50/50">
                                    <td className="px-5 py-3">
                                        <p className="font-semibold text-ink-950">{row.student_name}</p>
                                        <p className="text-xs text-ink-500">{row.cohort || row.student_email}</p>
                                    </td>
                                    <td className="px-5 py-3 text-ink-800">{row.challenge_title}</td>
                                    <td className="px-5 py-3 text-ink-700">{row.submitted_at ? formatDate(row.submitted_at) : '—'}</td>
                                    <td className="px-5 py-3">
                                        <StatusPill
                                            status={row.status}
                                            label={row.status === 'submitted' ? 'Submitted' : row.status === 'in_progress' ? `In progress · ${row.progress_percent}%` : undefined}
                                        />
                                    </td>
                                    <td className="px-5 py-3 text-ink-800">{row.score_max ? `${row.score_total} / ${row.score_max}` : '—'}</td>
                                    <td className="px-5 py-3 text-right">
                                        <RowAction row={row} onNudge={nudge} nudging={nudging === `${row.challenge_id}:${row.intern_user_id}`} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function RowAction({ row, onNudge, nudging }) {
    if (row.status === 'submitted') {
        return <Link to={`/mentor/challenge-reviews/${row.submission_id}`} className="btn-primary inline-flex">Review</Link>;
    }
    if (['changes_requested', 'approved'].includes(row.status)) {
        return <Link to={`/mentor/challenge-reviews/${row.submission_id}`} className="btn-secondary inline-flex">Open</Link>;
    }
    return (
        <button type="button" onClick={() => onNudge(row)} disabled={nudging} className="btn-secondary">
            {nudging ? 'Sending…' : 'Nudge'}
        </button>
    );
}
